import { Box, Card, CardContent, Stack, Typography, InputAdornment, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { FiEye, FiEyeOff } from "react-icons/fi";
import LabelInputField from "../../components/core-components/label-input-field/LabelInputField";
import CoreButtons from "../../components/core-components/button/CoreButtons";
import { useAppDispatch } from "../../hooks/storeHooks";
import { ROUTE_PATHS } from "../../routes";
import { resetPasswordThunk } from "../../store/user/userThunk";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import { useToast } from "../../context/ToasterContext";
import { RESET_PASSWORD_SCHEMA } from "../../utils/yup-schemas/yupSchemas";
import {
	PASSWORD_UPPERCASE_REGEX,
	PASSWORD_DIGIT_REGEX,
	PASSWORD_SPECIAL_CHAR_REGEX,
} from "../../utils/regex";
import palette from "../../theme/palette";
import typography from "../../theme/typography";
import IsMobile from "../../hooks/isMobile";
import { REDUX_REQUEST_STATUS } from "../../types/commonTypes";

type ResetPasswordForm = yup.InferType<typeof RESET_PASSWORD_SCHEMA>;

const HINTS = [
	{ key: "minLength", label: "Minimum of 8 characters", test: (v: string) => v.length >= 8 },
	{ key: "uppercase", label: "One uppercase letter", test: (v: string) => PASSWORD_UPPERCASE_REGEX.test(v) },
	{ key: "number", label: "One number", test: (v: string) => PASSWORD_DIGIT_REGEX.test(v) },
	{
		key: "special",
		label: "One special character",
		test: (v: string) => PASSWORD_SPECIAL_CHAR_REGEX.test(v),
	},
];

export default function ResetPasswordPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const isMobile = IsMobile();
	const token = searchParams.get("token");

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
	const [touched, setTouched] = useState<{ password?: boolean; confirmPassword?: boolean }>({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!token) {
			navigate(ROUTE_PATHS.LOGIN, { replace: true });
		}
	}, [token, navigate]);

	const validate = (values: ResetPasswordForm): boolean => {
		try {
			RESET_PASSWORD_SCHEMA.validateSync(values, { abortEarly: false });
			setErrors({});
			return true;
		} catch (err) {
			if (yup.ValidationError.isError(err)) {
				const next: Record<string, string> = {};
				err.inner.forEach((e) => {
					if (e.path) next[e.path] = e.message;
				});
				setErrors(next as { password?: string; confirmPassword?: string });
			}
			return false;
		}
	};

	const isValid =
		password.length >= 8 &&
		PASSWORD_UPPERCASE_REGEX.test(password) &&
		PASSWORD_DIGIT_REGEX.test(password) &&
		PASSWORD_SPECIAL_CHAR_REGEX.test(password) &&
		confirmPassword === password;

	const handleSubmit = async () => {
		if (!token) return;
		const values = { password, confirmPassword };
		if (!validate(values)) return;
		setLoading(true);
		const resp = await dispatch(resetPasswordThunk({ token, password }));
		setLoading(false);
		if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			showToast("Password reset successfully.");
			navigate(ROUTE_PATHS.LOGIN, { replace: true });
		} else {
			const msg =
				(resp.payload as { message?: string })?.message || "Something went wrong. Please try again.";
			showToast(msg, undefined, "error");
			setTimeout(() => {
				navigate(ROUTE_PATHS.FORGOT_PASSWORD);
			}, 300);
		}
	};

	if (!token) return null;

	return (
		<Card sx={{ width: "100%", backgroundColor: "transparent", boxShadow: "none", border: "none" }}>
			<CardContent sx={{ p: 3 }}>
				<Stack spacing={2} sx={{ alignItems: "center", mb: 4 }}>
					<Typography sx={{ textAlign: "center", fontFamily: typography.fontSecondary,
							fontSize: isMobile ? 32 : 38,
							fontWeight: 600,
							color: palette.secondary.main, }}>
						Set new password
					</Typography>
					<Typography
						variant="body1" sx={{ color: palette.grey[950], textAlign: "center", fontFamily: typography.fontPrimaryGeneralSans,
							fontWeight: typography.fontWeightRegular, }}>
						Enter your new password below and check the hint while setting it.
					</Typography>
				</Stack>
				<Box component="form" noValidate sx={{ maxWidth: 500, width: "100%", mx: "auto" }}>
					<LabelInputField
						id="password"
						label="New password"
						type={showPassword ? "text" : "password"}
						placeholder="Enter new password"
						value={password}
						onChange={(e) => {
							const nextPassword = e.target.value;
							setPassword(nextPassword);
							validate({ password: nextPassword, confirmPassword });
						}}
						onBlur={() => setTouched((t) => ({ ...t, password: true }))}
						error={touched.password && !!errors.password}
						helperText={''}
						className="form-group"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowPassword((p) => !p)}
										edge="end"
										size="small"
									>
										{showPassword ? <FiEyeOff /> : <FiEye />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Box component="ul" sx={{ mt: 1, pl: 2.5, mb: 2, listStyle: "disc" }}>
						{HINTS.map((h) => {
							const value = password;
							const satisfied = value.length > 0 && h.test(value);
							const failed = value.length > 0 && !h.test(value);
							const color = failed
								? palette.error.main
								: satisfied
									? palette.success.main
									: palette.grey[700];
							return (
								<Box
									component="li"
									key={h.key}
									sx={{
										fontSize: 16,
										color,
										mb: 0.5,
										fontFamily: typography.fontPrimaryGeneralSans,
										fontWeight: typography.fontWeightRegular,
									}}
								>
									{h.label}
								</Box>
							);
						})}
					</Box>
					<LabelInputField
						id="confirmPassword"
						label="Confirm password"
						type={showConfirmPassword ? "text" : "password"}
						placeholder="Confirm new password"
						value={confirmPassword}
						onChange={(e) => {
							const nextConfirm = e.target.value;
							setConfirmPassword(nextConfirm);
							validate({ password, confirmPassword: nextConfirm });
						}}
						onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
						error={
							(touched.confirmPassword && !!errors.confirmPassword) ||
							(confirmPassword.length > 0 && password !== confirmPassword)
						}
						errorTextOnly
						helperText={
							touched.confirmPassword
								? errors.confirmPassword
								: confirmPassword.length > 0 && password !== confirmPassword
									? "Confirm Password should match with the New Password"
									: undefined
						}
						className="form-group"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle confirm password visibility"
										onClick={() => setShowConfirmPassword((p) => !p)}
										edge="end"
										size="small"
									>
										{showConfirmPassword ? <FiEyeOff /> : <FiEye />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Box sx={{ mt: 2 }}>
						<CoreButtons
							buttonText="Submit"
							fullWidth
							coreBtnPill
							variant={BUTTON_VARIANT.CONTAINED}
							color={BUTTON_COLOR.PRIMARY}
							onclick={handleSubmit}
							disabled={!isValid}
							loading={loading}
						/>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
}
